import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

/**
 * Lista agrupada por prazo — Vencidas / Esta semana / Próximas. Cada grupo tem
 * um ponto colorido no título e o total (mono) à direita; o total vem PRONTO do
 * servidor, o cliente só o formata.
 *
 * ⚠️ GRUPO VAZIO NÃO RENDERIZA — exceto quem pedir `mostrarVazio`. Antes todos
 * os vazios apareciam, e o resultado era uma tela em que "Vencidas · 0 · R$ 0,00"
 * ocupava espaço para dar uma boa notícia que ninguém foi ali procurar. A exceção
 * que fica é "Esta semana": ela responde à pergunta que a pessoa abriu a tela para
 * fazer, então "Nada vence até dom 13/09" é resposta, não buraco.
 */
export interface GrupoAgenda {
  id: string;
  titulo: string;
  tom: "erro" | "atencao" | "neutro" | "sucesso";
  quantidade: number;
  /** "R$ 12.345,00" — já formatado. */
  totalFormatado: string;
  /** Texto do grupo vazio — só aparece com `mostrarVazio`. */
  vazio: string;
  /** Renderiza o grupo mesmo vazio, com a frase de `vazio`. Ver o ⚠️ acima. */
  mostrarVazio?: boolean;
  /** Quando o título é uma porta (o comprador, em "Por comprador"). */
  href?: string;
  children?: React.ReactNode;
}

const PONTO: Record<GrupoAgenda["tom"], string> = {
  erro: "bg-erro",
  atencao: "bg-atencao",
  neutro: "bg-muted-foreground",
  sucesso: "bg-sucesso",
};

/**
 * A ORDEM dos baldes de vencimento — a mesma nas TRÊS telas de dinheiro (A
 * receber do produtor, A pagar e A receber do comprador).
 *
 * ⚠️ VENCIDAS PRIMEIRO, sempre. A ordem antes dependia de ter conteúdo ("vencidas
 * por último quando vazio"), e o efeito colateral era o grupo mais urgente
 * renderizando VAZIO no fim da tela. Agora a ordem é fixa e o vazio não
 * renderiza — o que resolve os dois casos de uma vez.
 *
 * Mora aqui, e não em cada tela, porque três cópias de uma ordem divergem na
 * primeira mudança.
 */
export const ORDEM_VENCIMENTO = ["vencidas", "semana", "proximas", "semVencimento"] as const;
export type BaldeVencimento = typeof ORDEM_VENCIMENTO[number];

/** Quais grupos aparecem — ver o ⚠️ de `mostrarVazio`. Puro, para o teste. */
export function gruposVisiveis(grupos: GrupoAgenda[]): GrupoAgenda[] {
  return grupos.filter(g => g.quantidade > 0 || g.mostrarVazio);
}

export function ListaAgenda({ grupos, testId }: { grupos: GrupoAgenda[]; testId?: string }) {
  return (
    <div className="space-y-5" data-testid={testId}>
      {gruposVisiveis(grupos).map(g => (
        <section key={g.id} data-testid={`agenda-${g.id}`}>
          <header className="flex items-center justify-between gap-3 mb-2">
            <h2 className="text-sm font-semibold flex items-center gap-2 min-w-0">
              <span className={cn("h-2 w-2 rounded-full shrink-0", PONTO[g.tom])} />
              {g.href
                ? <TituloLink href={g.href} testId={`agenda-titulo-${g.id}`}>{g.titulo}</TituloLink>
                : <span className="truncate">{g.titulo}</span>}
              <span className="text-xs font-normal text-muted-foreground shrink-0">· {g.quantidade}</span>
            </h2>
            <span className="font-mono text-sm font-semibold tabular-nums">{g.totalFormatado}</span>
          </header>
          {g.quantidade === 0
            ? <p className="text-xs text-muted-foreground rounded-lg border border-dashed px-3 py-3">{g.vazio}</p>
            : g.children}
        </section>
      ))}
    </div>
  );
}

function TituloLink({ href, children, testId }: { href: string; children: React.ReactNode; testId?: string }) {
  const [, setLocation] = useLocation();
  return (
    <button
      type="button"
      onClick={() => setLocation(href)}
      className="truncate underline underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
      data-testid={testId}
    >
      {children}
    </button>
  );
}
